import { Component, lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import type { MotionValue } from "@/animation/gsap-motion";
import { afterPaint } from "@/lib/after-paint";
import { SkyToneIcon } from "@/components/ui/SkyToneIcon";
import { SKY_TONES, useSkyTheme } from "@/theme/sky-theme-core";
import { sceneStyles as styles } from "@/styles/classes";
import type { Locale } from "@/types";

const Scene = lazy(async () => ({ default: (await import("./UnifiedAirScene")).UnifiedAirScene }));
const labels = {
  en: { sunrise: "Sunrise", sun: "Day", sunset: "Sunset", moon: "Night" },
  zh: { sunrise: "日出", sun: "白昼", sunset: "日落", moon: "夜晚" },
};

class SkyBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() { return this.state.failed ? null : this.props.children; }
}

export function LandingSky({ locale, footerProgress }: { locale: Locale; footerProgress: MotionValue<number> }) {
  const { tone, setTone } = useSkyTheme();
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px), (pointer: coarse), (prefers-reduced-motion: reduce)");
    let cancel: (() => void) | undefined;
    const sync = () => {
      cancel?.();
      if (media.matches) setEnabled(false);
      else cancel = afterPaint(() => setEnabled(true));
    };
    sync();
    media.addEventListener("change", sync);
    return () => { cancel?.(); media.removeEventListener("change", sync); };
  }, []);

  return <>
    <div className={styles.scene} data-tone={tone} aria-hidden="true">
      <SkyBoundary><Suspense fallback={null}>{enabled && <Scene tone={tone} footerProgress={footerProgress} />}</Suspense></SkyBoundary>
    </div>
    <nav className={styles.toneRail} data-tone={tone} aria-label={locale === "zh" ? "天空时间" : "Sky time"}>
      {SKY_TONES.map((item) => <button key={item} type="button" className={styles.toneButton}
        aria-label={labels[locale][item]} aria-pressed={tone === item} title={labels[locale][item]}
        onClick={() => setTone(item)}><SkyToneIcon tone={item} /></button>)}
    </nav>
  </>;
}
